import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Stepper } from "./Reveal";
import { useCart } from "@/lib/cart";

export const ProductCard = ({ p, featured = false, index }) => {
  const { add, setOpen } = useCart();
  const [qty, setQty] = useState(1);
  const [color, setColor] = useState(p.colors?.[0] ?? null);

  const onAdd = () => {
    const key = color ? `${p.id}::${color}` : p.id;
    add(key, qty);
    toast.success(`${qty} × ${p.name}${color ? ` — ${color}` : ""} added`, { action: { label: "View cart", onClick: () => setOpen(true) } });
    setQty(1);
  };

  return (
    <motion.article
      data-testid={`product-card-${p.id}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: index * 0.12 }}
      className={`group relative flex h-full overflow-hidden rounded-[28px] bg-[#0F271D] text-[#F5F0E8] ${featured ? "flex-col" : "flex-col sm:flex-row"}`}
    >
      <div className={`relative flex items-center justify-center overflow-hidden ${featured ? "h-[340px] sm:h-[460px] lg:flex-1" : "h-[260px] sm:h-auto sm:w-[46%]"}`}>
        {featured ? (
          <div className="relative h-full aspect-square overflow-hidden">
            <img src={p.image} alt={`${p.full} — ${p.colour}`} className="absolute inset-0 h-full w-full scale-110 object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.18]" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F271D]/60 via-transparent to-transparent" />
          </div>
        ) : (
          <img src={p.images?.[color] || p.image} alt={`${p.full} — ${color || p.colour}`} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]" />
        )}
        {!featured && <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F271D]/50 via-transparent to-transparent" />}
        <span className="absolute left-5 top-5 rounded-full border border-[#F5F0E8]/25 bg-[#0B1D16]/50 px-3 py-1 font-mono text-[11px] uppercase tracking-widest backdrop-blur-md">
          {p.outOfStock ? "Out of stock" : p.tag}
        </span>
        <span className="absolute right-5 top-5 font-mono text-xs text-[#F5F0E8]/70">0{index + 1}</span>
      </div>
      <div className={`relative flex flex-col justify-between gap-6 p-6 sm:p-8 ${featured ? "" : "sm:flex-1"}`}>
        <div>
          <div className="flex items-end justify-between gap-4">
            <h3 className={`font-display font-black uppercase leading-[0.9] tracking-tight ${featured ? "text-6xl sm:text-7xl" : "text-5xl"}`}>{p.name}</h3>
            <div data-testid={`product-price-${p.id}`} className="font-mono text-2xl font-semibold text-[#D99B26]">${p.price}</div>
          </div>
          <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-[#F5F0E8]/70">{p.colour}</p>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#F5F0E8]/80">{p.blurb}</p>
          {p.colors && (
            <div className="mt-5 flex flex-wrap gap-2">
              {p.colors.map((c) => (
                <button
                  key={c}
                  data-testid={`product-${p.id}-color-${c.toLowerCase()}-button`}
                  onClick={() => setColor(c)}
                  className={`flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest transition-colors duration-300 ${color === c ? "border-[#D99B26] bg-[#D99B26] text-[#0F1412]" : "border-[#F5F0E8]/25 text-[#F5F0E8]/85 hover:border-[#F5F0E8]"}`}
                >
                  <span className="h-3 w-3 rounded-full" style={{ background: { Yellow: "#e9f51f", Green: "#3ecf3e", Pink: "#f24fb6", Orange: "#f2801f" }[c] }} />
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {p.outOfStock ? (
            <div data-testid={`product-${p.id}-outofstock-note`} className="flex-1 rounded-full border border-[#F5F0E8]/25 px-4 py-3 text-center font-mono text-xs uppercase tracking-widest text-[#F5F0E8]/85">
              Back in stock soon
            </div>
          ) : (
            <>
              <Stepper id={`product-${p.id}-qty`} value={qty} onChange={setQty} light />
              <button data-testid={`add-to-cart-${p.id}-button`} onClick={onAdd} className="btn-gold flex-1 justify-center whitespace-nowrap !px-5 !py-3">
                <Plus className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" /> Add to cart
              </button>
            </>
          )}
        </div>
      </div>
    </motion.article>
  );
};
